import { useCallback, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { Channel, Program } from "@hybrid-iptv/types";
import { fetchChannels, fetchEpgByName, fetchEpgFor, type EpgResponse } from "./api.js";
import { nativeGuideAvailable, rememberChannelNames } from "../api/nativeGuide.js";
import { isCategoryLocked, useParentalFilter } from "../settings/parentalStore.js";
import { readEpgCache, writeEpgCache } from "./epgCache.js";
import { useDeviceSeededQuery } from "../library/useDeviceSeededQuery.js";
import { catalogCacheKey } from "../storage/catalogCache.js";

/**
 * The guide window always starts on the hour (9 PM, 10 PM, …). Shared by the
 * grid (EpgGrid) and the D-pad focus logic (useGuideFocus) so both index the
 * same program window for a channel.
 */
export function epgWindowStart(now: Date): Date {
  const d = new Date(now);
  d.setMinutes(0, 0, 0);
  return d;
}

export interface ChannelProgramIndex {
  getNowPlaying: (channelId: string, at?: Date) => Program | null;
  getProgramsInWindow: (channelId: string, windowStart: Date, windowEnd: Date) => Program[];
  getProgramsForChannel: (channelId: string) => Program[];
  hasData: (channelId: string) => boolean;
  /** Every programme across every channel — the flat scan universal search
   * (Section 11) needs. Returns the index's own arrays, so treat as read-only:
   * copying 130k+ programmes on each keystroke would stall the UI. */
  allPrograms: () => Program[];
}

/** One channel's programmes, with their times parsed ONCE (see buildIndex). */
interface ChannelPrograms {
  list: Program[];
  starts: number[];
  ends: number[];
}

function buildIndex(programs: Program[]): ChannelProgramIndex {
  // Times are parsed here and nowhere else. They used to be parsed on every
  // probe — `new Date(p.start).getTime()` inside a binary search, so ~14 ISO
  // strings per call to getNowPlaying, and a linear scan with two parses per
  // programme in getProgramsInWindow. Profiled on a phone (2026-09-20) that was
  // 3.8 SECONDS of the app's startup, its single biggest cost. Parsing 130k
  // programmes once costs a fraction of that and every later lookup is integer
  // comparisons.
  const grouped = new Map<string, Array<{ p: Program; s: number; e: number }>>();
  for (const p of programs) {
    const arr = grouped.get(p.channelId) ?? [];
    arr.push({ p, s: Date.parse(p.start), e: Date.parse(p.end) });
    grouped.set(p.channelId, arr);
  }
  const byChannel = new Map<string, ChannelPrograms>();
  for (const [channelId, arr] of grouped) {
    arr.sort((a, b) => a.s - b.s);
    byChannel.set(channelId, {
      list: arr.map((x) => x.p),
      starts: arr.map((x) => x.s),
      ends: arr.map((x) => x.e),
    });
  }

  function getNowPlaying(channelId: string, at: Date = new Date()): Program | null {
    const channel = byChannel.get(channelId);
    if (!channel?.list.length) return null;
    const atTime = at.getTime();
    const { starts, ends, list } = channel;
    let lo = 0;
    let hi = starts.length - 1;
    let result = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (starts[mid]! <= atTime) {
        result = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    if (result === -1) return null;
    return ends[result]! > atTime ? list[result]! : null;
  }

  function getProgramsInWindow(channelId: string, windowStart: Date, windowEnd: Date): Program[] {
    const channel = byChannel.get(channelId);
    if (!channel?.list.length) return [];
    const ws = windowStart.getTime();
    const we = windowEnd.getTime();
    const { starts, ends, list } = channel;
    // Start just before the window: binary search for it rather than scanning
    // the channel's whole day. A few entries of slack covers a programme that
    // began earlier and is still running.
    let lo = 0;
    let hi = starts.length - 1;
    let first = starts.length;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (starts[mid]! >= ws) {
        first = mid;
        hi = mid - 1;
      } else {
        lo = mid + 1;
      }
    }
    const results: Program[] = [];
    for (let i = Math.max(0, first - 4); i < starts.length; i++) {
      if (starts[i]! >= we) break;
      if (ends[i]! > ws) results.push(list[i]!);
    }
    return results;
  }

  function getProgramsForChannel(channelId: string): Program[] {
    return byChannel.get(channelId)?.list ?? [];
  }

  function hasData(channelId: string): boolean {
    return byChannel.has(channelId);
  }

  // Flattened once and cached: the feed can carry 130k+ programmes, and search
  // re-runs on every keystroke — rebuilding this per query would be the
  // slowest thing in the app.
  let flattened: Program[] | null = null;
  function allPrograms(): Program[] {
    if (!flattened) flattened = [...byChannel.values()].flatMap((channel) => channel.list);
    return flattened;
  }

  return { getNowPlaying, getProgramsInWindow, getProgramsForChannel, hasData, allPrograms };
}

/** `ignoreParental`: see useVodCatalog — for the parental picker only. */
export function useChannels(sourceId: string, ignoreParental = false) {
  // Parental filtering happens HERE, in the shared select, so a locked category
  // never reaches Live, Home, Search or the guide — filtering per-page would
  // mean every new surface is a chance to forget one.
  const parental = useParentalFilter();
  // A STABLE select. React Query re-runs select whenever its identity changes,
  // and an inline arrow is a new identity on every render — so filtering,
  // sorting and grouping 10,777 channels ran again on each one, and handed
  // every consumer a brand-new array that invalidated their memos in turn.
  const select = useCallback(
    (channels: Channel[]) => {
      const visible = channels
        .filter((c) => !c.hidden && (ignoreParental || !isCategoryLocked(c.categoryId, parental)))
        .sort((a, b) => (a.number ?? 9999) - (b.number ?? 9999));
      const groups = new Map<string, Channel[]>();
      for (const ch of visible) {
        const arr = groups.get(ch.categoryId) ?? [];
        arr.push(ch);
        groups.set(ch.categoryId, arr);
      }
      return { channels: visible, groups };
    },
    [ignoreParental, parental],
  );
  return useDeviceSeededQuery({
    queryKey: ["channels", sourceId],
    queryFn: () => fetchChannels(sourceId),
    cacheStore: "channels",
    cacheKey: catalogCacheKey("channels", sourceId),
    sizeOf: (channels) => channels.length,
    staleTime: 1000 * 60 * 60,
    select,
  });
}

/**
 * Raw EPG query — fetches the full XMLTV feed filtered by channel names.
 * Returns the unfiltered query result. Callers use {@link useProgramIndex}
 * to map XMLTV IDs → our channel IDs and build the searchable index.
 *
 * The index is computed in a useMemo (not React Query's select) so it
 * re-computes whenever channels change — select is a pure function of
 * query data and won't re-run when closure variables like nameToOurId
 * are updated by streaming channel batches.
 */
export function useEpg(sourceId: string, channels: Channel[], windowHours: number, options?: { enabled?: boolean }) {
  const allowed = options?.enabled !== false;
  // Build channel name → our hashed ID. Not while switched off: in the APK every
  // useEpgFor holds one of these switched off, over ten thousand channels.
  const nameToOurId = useMemo(() => {
    const map = new Map<string, string>();
    if (!allowed) return map;
    for (const ch of channels) {
      const key = ch.name.toLowerCase().trim();
      if (key) map.set(key, ch.id);
    }
    return map;
  }, [channels, allowed]);

  const channelNames = useMemo(() => [...nameToOurId.keys()], [nameToOurId]);

  // Device cache probe: reads IndexedDB once (staleTime: Infinity — the
  // network query below is the live source). Its data becomes the network
  // query's placeholder, so the guide renders with last-known data the moment
  // the DB answers, while the background refresh runs concurrently.
  // windowHours is part of the key so switching 4h → 12h → 24h refetches
  // with the new window (and only that window's data is downloaded).
  const epgKey = ["epg", sourceId, "names", Math.floor(channels.length / 1000) * 1000, windowHours];
  const queryClient = useQueryClient();
  const cacheQuery = useQuery({
    queryKey: ["epg-cache", sourceId, windowHours],
    queryFn: () => readEpgCache(sourceId, windowHours),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    // Only while the live query has nothing to show. The probe is dropped from
    // memory five minutes after the last page using it unmounts, and it then
    // re-read the whole stored feed (tens of MB) on the next visit — although
    // the fresh feed was already in memory and the placeholder was never used.
    enabled: allowed && queryClient.getQueryData(epgKey) === undefined,
  });

  return useQuery({
    queryKey: epgKey,
    queryFn: async () => {
      const data = await fetchEpgByName(sourceId, channelNames, windowHours);
      // Write-through on every successful EPG upload: the newest feed's
      // fetchedAt becomes the version stamp, replacing any older cached feed.
      void writeEpgCache(sourceId, windowHours, data);
      return data;
    },
    staleTime: 1000 * 60 * 15,
    enabled: allowed && channelNames.length > 0,
    // Render the cached EPG instantly while the network refresh is in flight.
    // placeholderData is a function so it re-evaluates when the cache probe
    // resolves; the guide never waits on the network to paint.
    placeholderData: () => cacheQuery.data?.data,
  });
}

/**
 * Every channel's name as the guide matches it: lower-cased, trimmed, each once,
 * in order (useEpg's list). Worked out once per channel list: the guide's four
 * requests and the phone remote all ask with the same ten thousand channels.
 */
function matchNames(channels: readonly Channel[]): string[] {
  const held = heldNames.get(channels);
  if (held) return held;
  // The names as written, for guide entries read from a name (api/nativeGuide.ts).
  rememberChannelNames(channels);
  const names = new Set<string>();
  for (const ch of channels) {
    const key = ch.name.toLowerCase().trim();
    if (key) names.add(key);
  }
  const list = [...names];
  heldNames.set(channels, list);
  return list;
}

const heldNames = new WeakMap<readonly Channel[], string[]>();
const heldFingerprints = new WeakMap<readonly Channel[], string>();

const ONLY_STALE_MS = 1000 * 60 * 15;

function onlyKey(sourceId: string, channels: readonly Channel[], wanted: readonly Channel[], windowHours: number, details = true) {
  let fingerprint = heldFingerprints.get(wanted);
  if (fingerprint === undefined) {
    fingerprint = channelsFingerprint(wanted);
    heldFingerprints.set(wanted, fingerprint);
  }
  return ["epg", sourceId, "only", Math.floor(channels.length / 1000) * 1000, windowHours, fingerprint, details];
}

/**
 * The guide for the channels one screen shows (Phase 3 step 2a). In the APK the
 * native guide answers for `wanted` alone, so no screen holds the whole feed
 * (about 88,000 programmes on the owner's provider); the name match is still
 * made over every channel, so each channel gets the same programmes as before.
 * On the website it is useEpg, unchanged: the server answers the whole guide.
 *
 * `keepPrevious` shows the last answer while a changed set loads, for screens
 * whose set changes a little at a time (Home's recents). The guide's grid
 * leaves it off: another category's programmes would read as "no information".
 * `details: false` asks for titles and times only (useGuideEpg).
 */
export function useEpgFor(
  sourceId: string,
  channels: Channel[],
  wanted: readonly Channel[],
  windowHours: number,
  options?: { enabled?: boolean; keepPrevious?: boolean; details?: boolean },
) {
  const native = nativeGuideAvailable();
  const details = options?.details !== false;
  const allowed = options?.enabled !== false;
  const whole = useEpg(sourceId, channels, windowHours, { enabled: allowed && !native });
  const names = useMemo(() => matchNames(channels), [channels]);
  const onlyNames = useMemo(() => matchNames(wanted), [wanted]);
  const only = useQuery({
    queryKey: onlyKey(sourceId, channels, wanted, windowHours, details),
    queryFn: () => fetchEpgFor(sourceId, names, onlyNames, windowHours, details),
    staleTime: ONLY_STALE_MS,
    enabled: allowed && native && names.length > 0,
    ...(options?.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
  return native ? only : whole;
}

/**
 * The same answer as useEpgFor, asked outside a render (the phone remote asks
 * for the channels a request names), and indexed for those channels.
 */
export async function programIndexFor(
  queryClient: QueryClient,
  sourceId: string,
  channels: Channel[],
  wanted: readonly Channel[],
  windowHours: number,
): Promise<ChannelProgramIndex | null> {
  if (channels.length === 0) return null;
  const names = matchNames(channels);
  const data = await queryClient.fetchQuery<EpgResponse>(
    nativeGuideAvailable()
      ? {
          queryKey: onlyKey(sourceId, channels, wanted, windowHours),
          queryFn: () => fetchEpgFor(sourceId, names, matchNames(wanted), windowHours),
          staleTime: ONLY_STALE_MS,
        }
      : {
          queryKey: ["epg", sourceId, "names", Math.floor(channels.length / 1000) * 1000, windowHours],
          queryFn: () => fetchEpgByName(sourceId, names, windowHours),
          staleTime: 1000 * 60 * 15,
        },
  );
  return indexOf(data, wanted);
}

/**
 * Two indexes as one: `extra` answers for the channels `main` has no programmes
 * for (the guide's grid, and the playing channel when it is in another
 * category). Null while `main` loads, as the grid was before it had a guide.
 */
export function mergeProgramIndexes(
  main: ChannelProgramIndex | null,
  extra: ChannelProgramIndex | null,
): ChannelProgramIndex | null {
  if (!main || !extra) return main;
  const pick = (channelId: string) => (main.hasData(channelId) ? main : extra);
  return {
    getNowPlaying: (channelId, at) => pick(channelId).getNowPlaying(channelId, at),
    getProgramsInWindow: (channelId, start, end) => pick(channelId).getProgramsInWindow(channelId, start, end),
    getProgramsForChannel: (channelId) => pick(channelId).getProgramsForChannel(channelId),
    hasData: (channelId) => main.hasData(channelId) || extra.hasData(channelId),
    allPrograms: () => [...main.allPrograms(), ...extra.allPrograms()],
  };
}

/** Hours the guide loads first: the same window Home loads, so it is often in memory already. */
export const GUIDE_FIRST_HOURS = 4;
/** The grid asks for the far hours (EpgGrid, when scrolled toward the end of the first ones). */
const farRequests = new Set<() => void>();
export function requestGuideFar(): void {
  for (const request of farRequests) request();
}

/**
 * The guide's listings: GUIDE_FIRST_HOURS at once, then the full reach
 * (GUIDE_REACH_HOURS) in the background.
 *
 * TITLES FIRST (onn box, 2026-10-21). In the APK each category is asked for as
 * it is shown (useEpgFor), and with descriptions a big category's answer took
 * 0.3 s to cross from the TV and 0.35 s more to read on the page, so the grid
 * filled 0.5-0.9 s after a category was first picked. The grid shows titles and
 * times only, so they come first and the descriptions (preview, Info, a
 * programme's page) follow in a second answer that replaces it.
 *
 * WHY TWO STEPS (onn box, 2026-10-05). Loading all 36 hours before showing the
 * grid took the guide from 2.2 s to 5.8-8.6 s to show its programmes: every open
 * read the whole 36-hour feed from the device cache and indexed it. The first
 * hours are what the viewer looks at; the rest only matters once they scroll
 * right. So the guide opens on 4 hours as fast as before, and the rest loads
 * only when the viewer scrolls toward it (requestGuideFar). Loading it on a
 * timer instead froze the guide for ~1 s twice, 7-9 s after it opened, while
 * the viewer was still browsing the first hours.
 */
export function useGuideEpg(sourceId: string, channels: Channel[], wanted: readonly Channel[], reachHours: number) {
  const titles = useEpgFor(sourceId, channels, wanted, GUIDE_FIRST_HOURS, { details: false });
  const near = useEpgFor(sourceId, channels, wanted, GUIDE_FIRST_HOURS, { enabled: titles.data !== undefined });
  const [loadFar, setLoadFar] = useState(false);
  useEffect(() => {
    if (loadFar) return;
    const request = () => setLoadFar(true);
    farRequests.add(request);
    return () => {
      farRequests.delete(request);
    };
  }, [loadFar]);
  const far = useEpgFor(sourceId, channels, wanted, reachHours, { enabled: loadFar });
  return far.data !== undefined ? far : near.data !== undefined ? near : titles;
}

type EpgIndexInput = { programs: Program[]; nameMapping?: Record<string, string[]>; channels?: unknown; cached?: boolean };

/**
 * Indexes already built, per guide feed, so a page that comes back to the same
 * feed and channels does not build it again.
 *
 * THE COST (projector, 2026-09-24): Home, the guide and Catch-up each built
 * their own index from the whole feed on every visit — ~0.7 s of a 4.5 s block
 * measured on Back from the guide to Home, while Back appeared to do nothing
 * and the viewer pressed it again. The feed object is the query cache's, so it
 * is the same object on every page that asks for the same window; keyed
 * weakly, an index is dropped with its feed and never outlives it.
 */
const indexCache = new WeakMap<object, Map<string, ChannelProgramIndex>>();

/** A fingerprint of the channels an index maps onto: their ids and the names it matches by. */
function channelsFingerprint(channels: readonly Channel[]): string {
  let hash = 0x811c9dc5;
  for (const ch of channels) {
    for (const text of [ch.id, ch.name]) {
      for (let i = 0; i < text.length; i++) hash = Math.imul(hash ^ text.charCodeAt(i), 0x01000193);
      hash = Math.imul(hash ^ 0x1f, 0x01000193);
    }
  }
  return `${channels.length}:${hash >>> 0}`;
}

/**
 * Build a searchable program index from raw EPG data + the current channel
 * catalog. Reacts to both query-data changes AND channel-list changes, so
 * streaming channel batches don't leave the index stale.
 */
export function useProgramIndex(epgData: EpgIndexInput | undefined, channels: readonly Channel[]): ChannelProgramIndex | null {
  const fingerprint = useMemo(() => channelsFingerprint(channels), [channels]);

  return useMemo(
    () => (epgData ? indexOf(epgData, channels, fingerprint) : null),
    // `channels` is covered by its fingerprint: a new array with the same
    // channels in it must not rebuild the index.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [epgData, fingerprint],
  );
}

/** One feed's index for some channels, built once per feed and channel set (indexCache). */
function indexOf(epgData: EpgIndexInput, channels: readonly Channel[], fingerprint = channelsFingerprint(channels)): ChannelProgramIndex {
  const held = indexCache.get(epgData) ?? new Map<string, ChannelProgramIndex>();
  const hit = held.get(fingerprint);
  if (hit) return hit;
  const index = indexFeed(epgData, channels);
  held.set(fingerprint, index);
  indexCache.set(epgData, held);
  return index;
}

function indexFeed(epgData: EpgIndexInput, channels: readonly Channel[]): ChannelProgramIndex {
  const nameToOurId = new Map<string, string>();
  for (const ch of channels) {
    const key = ch.name.toLowerCase().trim();
    if (key) nameToOurId.set(key, ch.id);
  }

  // Map XMLTV channel IDs → our hashed channel IDs via nameMapping. One
  // feed channel can back several catalog channels ("USA ESPN" and
  // "USA ESPN UHD" both resolving to the same XMLTV channel), so each
  // program is duplicated to every catalog channel that maps to it.
  const xmltvIdToOurIds = new Map<string, string[]>();
  if (epgData.nameMapping) {
    // Only the feed channels this answer has programmes for: in the APK a
    // screen's answer covers its own channels, while the mapping covers all
    // ten thousand (Phase 3 step 2a).
    const mapping = epgData.nameMapping;
    const inAnswer = new Set<string>();
    for (const p of epgData.programs) inAnswer.add(p.channelId);
    for (const xmltvId of inAnswer) {
      const reqNames = mapping[xmltvId];
      if (!reqNames) continue;
      const ourIds: string[] = [];
      for (const reqName of reqNames) {
        const ourId = nameToOurId.get(reqName.toLowerCase().trim());
        if (ourId && !ourIds.includes(ourId)) ourIds.push(ourId);
      }
      if (ourIds.length > 0) xmltvIdToOurIds.set(xmltvId, ourIds);
    }
  }

  const programs: Program[] = [];
  for (const p of epgData.programs) {
    const ourIds = xmltvIdToOurIds.get(p.channelId);
    if (!ourIds) continue;
    for (const ourId of ourIds) {
      programs.push({ ...p, channelId: ourId });
    }
  }

  return buildIndex(programs);
}
